import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useResourceKit } from '@resource-kit/core/react';
import type { BulkActionDefinition } from '@resource-kit/model';
import { resourceKeys } from '../query/keys';
import type { ResourceHost } from './bundle';

export interface ResourceBulkActions<T> {
  selectedRows: T[];
  setSelectedRows: (rows: T[]) => void;
  /** Bump signal that clears the table's internal selection. */
  selectionResetKey: number;
  isPending: boolean;
  /** Actions the caller is permitted to run; empty when there are none. */
  actions: BulkActionDefinition<T>[];
  /** True when ANY selected row fails the action's eligibility predicate. */
  isIneligible: (action: BulkActionDefinition<T>) => boolean;
  run: (action: BulkActionDefinition<T>) => Promise<void>;
}

/**
 * Selection + bulk dispatch for a resource list (#1801, extracted from
 * ResourcePage).
 *
 * The table clears its internal selection on TWO independent triggers, both of
 * which flow back through `setSelectedRows`:
 *   1. a query-context change (page / sort / search / filter), owned by the
 *      table itself — the page-local selection contract;
 *   2. this hook's post-action reset. It is NOT redundant with (1): a bulk
 *      action need not change any query param, so the context key may be
 *      unchanged and only this signal clears the rows just acted on.
 */
interface ResourceBulkActionsBundle<T> {
  host: {
    slug: ResourceHost<T>['slug'];
    resourceBulkActions?: ResourceHost<T>['resourceBulkActions'];
  };
}

interface ResourceBulkActionsOptions {
  hasGrant: (permission: string | undefined) => boolean;
}

export function useResourceBulkActions<T>(
  bundle: ResourceBulkActionsBundle<T>,
  options: ResourceBulkActionsOptions,
): ResourceBulkActions<T> {
  const { host } = bundle;
  const { hasGrant } = options;
  const { t, confirm, notify } = useResourceKit();
  const queryClient = useQueryClient();

  const [selectedRows, setSelectedRows] = useState<T[]>([]);
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  // In-flight guard. The ref blocks a second synchronous dispatch
  // (double-click) before React commits the pending state; the state drives
  // the button's disabled UI. Both are needed: the ref for correctness, the
  // state for the visible pending signal.
  const inFlightRef = useRef(false);
  const [isPending, setIsPending] = useState(false);

  // Only actions the caller is permitted to run reach the toolbar (#1152).
  // An action without a declared `permission` is ungated. The filter is cheap
  // (a handful of actions) so it runs each render rather than carrying a memo
  // whose deps must track `permissions`.
  const actions = host.resourceBulkActions?.filter((a) => hasGrant(a.permission)) ?? [];

  const isIneligible = useCallback(
    (action: BulkActionDefinition<T>) =>
      selectedRows.some((row) => action.isEligible != null && !action.isEligible(row)),
    [selectedRows],
  );

  const run = useCallback(
    async (action: BulkActionDefinition<T>) => {
      if (selectedRows.length === 0 || isIneligible(action)) return;
      // Re-check the grant at execution time (#1152 §3): the button is
      // permission-gated at render, but a grant revoked between paint and
      // click must not slip a mutating call through the client.
      if (!hasGrant(action.permission)) return;

      // Reject re-entry while a dispatch is already running (double-click
      // on a no-confirm action, or a click during the confirm dialog).
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      setIsPending(true);

      try {
        if (action.confirm) {
          // Thread the selection size into BOTH confirm strings so a
          // bulk action's copy can interpolate `{{count}}` — general
          // to every bulk resource, and load-bearing for a
          // destructive one whose copy must state how many rows it
          // will remove. Keys without the placeholder are unaffected.
          const yes = await confirm({
            title: t(action.confirm.title, {
              defaultValue: action.confirm.title,
              count: selectedRows.length,
            }),
            message: action.confirm.description
              ? t(action.confirm.description, {
                  defaultValue: action.confirm.description,
                  count: selectedRows.length,
                })
              : t('rk.resource.proceedWith', { count: selectedRows.length }),
            // Absent ⇒ undefined ⇒ the host dialog's generic button label.
            confirmText: action.confirm.confirmText
              ? t(action.confirm.confirmText, { defaultValue: action.confirm.confirmText })
              : undefined,
            cancelText: action.confirm.cancelText
              ? t(action.confirm.cancelText, { defaultValue: action.confirm.cancelText })
              : undefined,
            intent: action.intent === 'danger' ? 'danger' : 'default',
          });
          if (!yes) return;
        }

        const result = await action.onAction(selectedRows);
        const message = action.formatResult
          ? action.formatResult(result)
          : t('rk.resource.actionCompleted');
        const actionLabel = t(action.label, { defaultValue: action.label });
        notify('info', actionLabel, message);
        // Invalidate the list AND any open detail cache: a bulk
        // mutation can change a record shown in the detail sheet, so a
        // list-only refetch would leave the drawer stale.
        queryClient.invalidateQueries({ queryKey: resourceKeys(host.slug).all() });
        setSelectionResetKey((k) => k + 1);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : t('rk.error.unknown');
        const actionLabel = t(action.label, { defaultValue: action.label });
        notify('error', t('rk.resource.actionFailed', { name: actionLabel }), msg);
      } finally {
        inFlightRef.current = false;
        setIsPending(false);
      }
    },
    [selectedRows, isIneligible, hasGrant, confirm, t, notify, queryClient, host.slug],
  );

  return {
    selectedRows,
    setSelectedRows,
    selectionResetKey,
    isPending,
    actions,
    isIneligible,
    run,
  };
}
